import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { DRS_CACHE_CONFIG, DRS_QUERY_KEYS } from "@/governance/constants/drs"
import { DelegateReputationScoreService } from "@/governance/helpers/drs"

type DRSContextValue = {
  service: DelegateReputationScoreService | null
  isReady: boolean
  isLoading: boolean
  orgId?: string
}

const defaultContextValue: DRSContextValue = {
  service: null,
  isReady: false,
  isLoading: false,
  orgId: undefined,
}

const DRSContext = createContext<DRSContextValue>(defaultContextValue)

type DRSProviderProps = {
  orgId?: string
  children: ReactNode
}

/**
 * Provider component that manages DRS service lifecycle
 */
export const DRSProvider: FC<DRSProviderProps> = ({ orgId, children }) => {
  const [isReady, setIsReady] = useState(false)
  const serviceRef = useRef<DelegateReputationScoreService | null>(null)
  const initializationRef = useRef<Promise<void> | null>(null)

  // Get or create service
  const service = useMemo(() => {
    if (!orgId) return null

    // If we already have a service for this orgId, return it
    if (serviceRef.current && serviceRef.current.orgId === orgId) {
      return serviceRef.current
    }

    // Create new service
    const newService = new DelegateReputationScoreService(orgId)
    serviceRef.current = newService
    return newService
  }, [orgId])

  // Memoize initialization function
  const initializeService = useCallback(async () => {
    if (!service || !orgId) return

    // If there's an ongoing initialization, wait for it
    if (initializationRef.current) {
      try {
        await initializationRef.current
        setIsReady(true)
      } catch (error) {
        console.error("Error waiting for DRS service initialization", error)
        setIsReady(false)
      }
      return
    }

    // Initialize service
    setIsReady(false)
    initializationRef.current = service.initialize().then(() => {
      initializationRef.current = null
    })

    try {
      await initializationRef.current
      setIsReady(true)
    } catch (error) {
      console.error("Error initializing DRS service", error)
      initializationRef.current = null
      setIsReady(false)
    }
  }, [service, orgId])

  useEffect(() => {
    initializeService()
  }, [initializeService])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      serviceRef.current = null
      initializationRef.current = null
    }
  }, [])

  const value = useMemo(
    () => ({
      service,
      isReady,
      isLoading: !isReady,
      orgId,
    }),
    [service, isReady, orgId],
  )

  return <DRSContext.Provider value={value}>{children}</DRSContext.Provider>
}

/**
 * Hook to access DRS context
 * @throws Error if used outside of DRSProvider
 */
export const useDRS = () => {
  return useContext(DRSContext)
}

/**
 * Hook to get DRS info for a single user
 */
export const useUserDRS = (address?: string | null) => {
  const context = useDRS()
  const { service, orgId } = context

  return useQuery({
    queryKey: [DRS_QUERY_KEYS.USER, orgId, address],
    queryFn: async () => {
      return await service?.getDRSInfo(address ?? "")
    },
    enabled: !!address && !!orgId && !!service,
    staleTime: DRS_CACHE_CONFIG.STALE_TIME_MS,
  })
}

/**
 * Hook to get DRS info for multiple users
 */
export const useBulkDRS = (addresses?: string[]) => {
  const context = useDRS()
  const { service, orgId } = context
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [DRS_QUERY_KEYS.BULK, orgId, addresses],
    queryFn: async () => {
      const response = await service?.getBulkDRSInfo(addresses ?? [])
      try {
        response?.forEach((drsInfo, address) => {
          queryClient.setQueryData(
            [DRS_QUERY_KEYS.USER, orgId, address.toLowerCase()],
            drsInfo,
          )
        })
      } catch (_e: unknown) {
        console.error("Error setting bulk DRS data", _e)
      }
      return response
    },
    enabled: !!addresses && !!orgId && !!service,
    staleTime: DRS_CACHE_CONFIG.STALE_TIME_MS,
  })
}
